import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import './styles/index.css';

// Configure React Query with offline support
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep cache for offline
      retry: (failureCount) => {
        // Don't retry if offline
        if (!navigator.onLine) return false;
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true, // Refetch when coming back online
      networkMode: 'offlineFirst', // Use cache when offline
    },
    mutations: {
      retry: false,
      networkMode: 'offlineFirst',
    },
  },
});

// Register Service Worker for PWA/offline support
const updateSW = registerSW({
  onNeedRefresh() {
    // Show update notification to user
    if (confirm('New version available! Reload to update?')) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    console.log('[PWA] App ready to work offline');
    // Could show a toast notification here
  },
  onRegistered(registration) {
    console.log('[PWA] Service Worker registered:', registration);

    // Check for updates every hour
    if (registration) {
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);
    }
  },
  onRegisterError(error) {
    console.error('[PWA] Service Worker registration error:', error);
  },
});

// Initialize offline database
import('./services/offline/db').then(({ getDB }) => {
  getDB().then(() => {
    console.log('[Offline] IndexedDB initialized');
  });
});

// Handle beforeinstallprompt for PWA install
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  // Store in window for the InstallPrompt component to access
  (window as unknown as { deferredInstallPrompt: Event }).deferredInstallPrompt = e;
  console.log('[PWA] Install prompt deferred');
});

// Track app installation
window.addEventListener('appinstalled', () => {
  console.log('[PWA] App installed successfully');
  delete (window as unknown as { deferredInstallPrompt?: Event }).deferredInstallPrompt;
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);
